import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useAuthOrigin } from '../lib/auth-origin';
import { ArrowRight } from './icons';
import { track } from '../lib/funnel';
import { countryConfig } from '../country';
import { sourceDataIsPublic } from '@tenders/shared';
import { FreeUnlockGate, type FreeUnlockData } from './free-unlock-gate';
import { LockGlyph } from './locked-chip';

export type TenderGateState = 'guest' | 'free' | 'pro';

/** Spain (`features.marketFirstPitch`, review 21.10 «first hour», item 3): the
 *  market row — who wins, for how much — leads the card; the documents and the
 *  contact follow, still padlocked. UZ/KZ keep their order. */
const MARKET_FIRST = countryConfig.features.marketFirstPitch === true;

/**
 * The single inline gate for a tender's paid content (documents, contacts,
 * analytics). ONE card:
 *
 *  - `guest`  → register CTA (the free tier's real value: saved searches +
 *               Telegram alerts) with a secondary Pro pitch.
 *  - `free`   → the locked rows PLUS the free-tier action area ({@link
 *               FreeUnlockGate}) — the one-per-month free unlock, with «оформить
 *               Pro» as its secondary. Folding the free-open button and the Pro
 *               pitch into this same card is deliberate: they used to be two
 *               stacked cards each ending in an «Оформить Pro» button, which read
 *               as redundant. A viewer with no `freeUnlock` payload (defensive)
 *               falls back to the plain Pro CTA.
 *  - `pro`    → renders nothing; the caller shows the unlocked content instead.
 *
 * Carries the `.tender-paid` selector target of the JSON-LD paywall markup
 * (added by the caller's wrapper). The AI summary is NOT repeated here — the
 * standalone AI-summary card above the gate already shows the faded teaser.
 */
export function TenderGateCard(props: {
  state: TenderGateState;
  freeUnlock?: FreeUnlockData | null;
  onUnlock?: () => void | Promise<void>;
  /** The lot HAS documents / a buyer contact (`TenderView.hasDocuments` /
   *  `hasContact`). A row is listed only for what exists: «Тендерная
   *  документация» over a lot with no files is a promise Pro then does not
   *  keep (owner, 2026-10-15: «на превью данные есть, я покупаю, а здесь их
   *  нет»). Absent = listed, as before. */
  hasDocuments?: boolean;
  hasContact?: boolean;
}) {
  const { state, freeUnlock, onUnlock, hasDocuments = true, hasContact = true } = props;
  const t = useT();
  // The lot this card sits on — a guest who registers from here comes back to
  // it rather than to the catalogue (`postAuthPath`).
  const authOrigin = useAuthOrigin();
  const publicSource = sourceDataIsPublic(countryConfig);

  if (state === 'pro') return null;

  const ctaClassName =
    // min-h rather than a fixed h: the guest label («Регистрируйтесь бесплатно —
    // сохранённые поиски и Telegram-алерты») wraps to three lines on a phone, and
    // with a fixed height the card's `overflow-hidden` clipped the last line.
    'inline-flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] py-2.5 text-center text-[14px] font-bold leading-snug text-white transition-colors hover:bg-brand-mid';

  const analyticsRow = (
    <li className="flex items-center gap-1.5">
      <LockGlyph />
      {t('tender.gate.lockedAnalytics')}
    </li>
  );

  return (
    <div className="relative overflow-hidden rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <h2 className="mb-2 text-[15px] font-bold text-ink">{t('tender.gate.title')}</h2>

      <ul className="mb-4 flex flex-col gap-1.5 text-[13px] text-ink-soft">
        {MARKET_FIRST && analyticsRow}
        {/* Where the source publishes the files and the contact
            (`features.publicSourceData`) neither is behind this wall — both are on
            the page for everyone — so the card lists only the analytics row below.
            It used to list an AI read of the pliegos there (analysis, questions, a
            fit check); no such surface exists, and the owner forbids selling one
            (2026-10-20). Spain sells the rows below, the Uzbek set, since then. */}
        {!publicSource && (
          <>
            {/* Summary/requirements are FREE now — the paywall is contacts / documents
                / analytics only (the tools to ACT on a tender, not to read it). */}
            {hasDocuments && (
              <li className="flex items-center gap-1.5">
                <LockGlyph />
                {t('tender.gate.lockedDocs')}
              </li>
            )}
            {hasContact && (
              <li className="flex items-center gap-1.5">
                <LockGlyph />
                {t('tender.gate.lockedContacts')}
              </li>
            )}
            {/* Winner phone/email — Pro-only (see /plans). UZ only, same reason as
                the /plans bullet: KZ holds no such data. */}
            {countryConfig.code === 'uz' && (
              <li className="flex items-center gap-1.5">
                <LockGlyph />
                {t('tender.gate.lockedWinnerContacts')}
              </li>
            )}
          </>
        )}
        {!MARKET_FIRST && analyticsRow}
      </ul>

      {state === 'guest' && (
        <>
          <Link to="/register" state={authOrigin} className={ctaClassName}>
            {t('tender.gate.guest')}
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
          </Link>
        </>
      )}

      {state === 'free' &&
        (freeUnlock && onUnlock ? (
          // The free-tier action: the monthly unlock button (+ «оформить Pro»
          // under it), or the ИНН/phone steps that make it available. Same card.
          <FreeUnlockGate freeUnlock={freeUnlock} onUnlock={onUnlock} />
        ) : (
          // Defensive fallback — no `freeUnlock` payload (should not happen for an
          // authed free viewer). Plain Pro CTA, desktop only: the mobile sticky
          // bottom bar already carries an identical one.
          <>
            <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">
              {t('tender.gate.free')}
            </p>
            <Link
              to="/plans"
              onClick={() => track('upgrade_cta_clicked', { source: 'tenderGate' })}
              className={`${ctaClassName} hidden lg:inline-flex`}
            >
              {t('tender.cta.pro')}
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
            </Link>
          </>
        ))}

      {/* No owner's Telegram line here (owner, 2026-10-11). A quiet link sat at
          the foot of this card (written 2026-10-06, live 2026-10-10…11) and was
          taken out: a second, easier action at the exact point where the reader
          decides to pay. The handle stays in the help/contact surfaces only;
          pre-sale questions go to the bot assistant. Do not re-add it without
          the owner's word. */}
    </div>
  );
}
